import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import './variables.css';
import './classes.css';


const getSystemTheme = () =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const ThemeContext = createContext({
    theme: 'system',
    resolvedTheme: 'light',
    setTheme: () => { },
    toggleTheme: () => { },
    useTheme: () => { },
    getColor: () => { },
    colors: {},
});

AppThemeProvider.displayName = 'AppThemeProvider';
export function AppThemeProvider({ children }) {
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'system');
    const [resolvedTheme, setResolvedTheme] = useState(theme === 'system' ? getSystemTheme() : theme);
    const [colors, setColors] = useState(initColors());

    function initColors() {
        const el = document.documentElement;
        const css = getComputedStyle(el);

        const keys =
            typeof el.computedStyleMap === 'function'
                ? Array.from(el.computedStyleMap().keys()).filter((k) => k.startsWith('--color-'))
                : Array.from(css).filter((key) => key.startsWith('--color-'));

        return keys.reduce((acc, key) => {
            acc[key.slice(8)] = css.getPropertyValue(key).trim();
            return acc;
        }, {});
    }

    function toggleTheme() {
        setTheme(pre => {
            let current = pre;
            if (current === 'system') current = getSystemTheme();
            return current === 'light' ? 'dark' : 'light';
        });
    }

    function getColor(name, opacity = 1) {
        if (!colors[name]) return '';
        if (opacity === 1) return colors[name];
        return colors[name].slice(0, 7) + `${Math.floor(opacity * 255).toString(16).padStart(2, '0')}`;
    }

    const updateDOM = useCallback((newResolvedTheme) => {
        document.documentElement.setAttribute('data-theme', newResolvedTheme);
        setTimeout(() => setColors(initColors()), 0);
    }, []);

    useEffect(() => {
        const newResolvedTheme = theme === 'system' ? getSystemTheme() : theme;
        setResolvedTheme(newResolvedTheme);
        localStorage.setItem('theme', theme);
        updateDOM(newResolvedTheme);
    }, [theme, updateDOM]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemChange = (e) => {
            if (theme === 'system') {
                const systemTheme = e.matches ? 'dark' : 'light';
                setResolvedTheme(systemTheme);
                updateDOM(systemTheme);
            }
        };
        mediaQuery.addEventListener('change', handleSystemChange);
        return () => mediaQuery.removeEventListener('change', handleSystemChange);
    }, [theme, updateDOM]);


    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme, colors, getColor }}>
            {children}
        </ThemeContext.Provider>
    );
}


export function useAppTheme() {
    return useContext(ThemeContext);
}
